import type { Command } from "../document/commands.ts";
import { foldDoors } from "../document/doors.ts";
import { COLLISION_CELLS_PER_TILE, type DoorDefinition, type DoorLayer, type EntityInstance, type JsonValue, type MapDocument, type WorldDocument } from "../document/types.ts";

/**
 * "Make the way back" (ADR-122 decision 2.4; its note "how P1.4-T138 is built", item 6): from a painted door, one
 * compound command that places an arrival point a tile outside the door on its own map, optionally a second arrival
 * point on the destination, a door layer on the destination when it has none, and a one-cell door a tile from the
 * destination's arrival point leading back to the new arrival point, carrying a copy of the first door's fields so a
 * pair starts agreeing. One undo takes it all back. Ids, names and the new arrival points' fields are the caller's:
 * the editor mints ids, and only the game knows what an arrival point's fields must hold.
 */

/**
 * A side of a door or an arrival point.
 */
export type WayBackSide = "up" | "down" | "left" | "right";

/**
 * What the way back is made from: the door (its map and id), the side of the door its new arrival point stands on
 * (`outside`), the side of the destination's arrival point the way back stands on (`behind`), the new arrival
 * point's id and fields, the way back's id and name, the layers to add on a map that lacks one (a marker layer on
 * the door's map, a door layer on the destination), and an optional second arrival point on the destination.
 */
export interface WayBackPlan {
  readonly mapId: string;
  readonly doorId: string;
  readonly outside: WayBackSide;
  readonly behind: WayBackSide;
  readonly arrival: { readonly id: string; readonly fields: { readonly [key: string]: JsonValue } };
  readonly door: { readonly id: string; readonly name: string };
  readonly markerLayer: { readonly id: string; readonly name: string };
  readonly doorLayer: { readonly id: string; readonly name: string };
  readonly second?: { readonly id: string; readonly x: number; readonly y: number; readonly fields: { readonly [key: string]: JsonValue } };
}

/**
 * The step of a side, in cells.
 */
const STEPS: Readonly<Record<WayBackSide, readonly [number, number]>> = { up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0] };

/**
 * A map of a world.
 *
 * @param world The world.
 * @param mapId The map.
 * @returns The map.
 * @throws Error when the world holds no such map.
 */
function requireMap(world: WorldDocument, mapId: string): MapDocument {
  const map = world.maps.find((candidate) => candidate.id === mapId);
  if (map === undefined) {
    throw new Error(`the world holds no map ${mapId}`);
  }
  return map;
}

/**
 * The box of the cells a door paints on its layer, in collision cells.
 *
 * @param map The door's map.
 * @param layer Its layer.
 * @param door The door.
 * @returns The first and last column and row.
 * @throws Error when it paints none.
 */
function paintedBox(map: MapDocument, layer: DoorLayer, door: DoorDefinition): { x0: number; y0: number; x1: number; y1: number } {
  const value = layer.doors.indexOf(door) + 1;
  const width = map.width * COLLISION_CELLS_PER_TILE;
  let box: { x0: number; y0: number; x1: number; y1: number } | null = null;
  for (let i = 0; i < layer.cells.length; i += 1) {
    if (layer.cells[i] !== value) {
      continue;
    }
    const x = i % width;
    const y = (i - x) / width;
    box = box === null ? { x0: x, y0: y, x1: x, y1: y } : { x0: Math.min(box.x0, x), y0: box.y0, x1: Math.max(box.x1, x), y1: y };
  }
  if (box === null) {
    throw new Error(`door ${door.id} paints no cell; paint it before making the way back`);
  }
  return box;
}

/**
 * Where the new arrival point stands: a tile beyond the door's painted cells on the side, at their middle, in tiles.
 *
 * @param box The door's painted cells.
 * @param side The side.
 * @returns The position.
 */
function outsideOf(box: { readonly x0: number; readonly y0: number; readonly x1: number; readonly y1: number }, side: WayBackSide): { x: number; y: number } {
  const midX = (box.x0 + box.x1 + 1) / 2 / COLLISION_CELLS_PER_TILE;
  const midY = (box.y0 + box.y1 + 1) / 2 / COLLISION_CELLS_PER_TILE;
  switch (side) {
    case "up":
      return { x: midX, y: box.y0 / COLLISION_CELLS_PER_TILE - 0.5 };
    case "down":
      return { x: midX, y: (box.y1 + 1) / COLLISION_CELLS_PER_TILE + 0.5 };
    case "left":
      return { x: box.x0 / COLLISION_CELLS_PER_TILE - 0.5, y: midY };
    case "right":
      return { x: (box.x1 + 1) / COLLISION_CELLS_PER_TILE + 0.5, y: midY };
  }
}

/**
 * Whether a position in tiles lies on a map.
 *
 * @param map The map.
 * @param x The column in tiles.
 * @param y The row in tiles.
 * @returns True when it does.
 */
function onMap(map: MapDocument, x: number, y: number): boolean {
  return x >= 0 && y >= 0 && x < map.width && y < map.height;
}

/**
 * The commands that put a marker on a map: into its first marker layer, or into a new one the plan names.
 *
 * @param map The map.
 * @param entity The marker.
 * @param layer The layer to add when the map holds none.
 * @returns The commands.
 */
function placeMarker(map: MapDocument, entity: EntityInstance, layer: { readonly id: string; readonly name: string }): Command[] {
  const held = map.layers.find((candidate) => candidate.kind === "entity");
  if (held?.kind === "entity") {
    return [{ kind: "addEntity", mapId: map.id, layerId: held.id, index: held.entities.length, entity }];
  }
  return [
    { kind: "addLayer", mapId: map.id, index: map.layers.length, layer: { kind: "entity", id: layer.id, name: layer.name, entities: [] } },
    { kind: "addEntity", mapId: map.id, layerId: layer.id, index: 0, entity },
  ];
}

/**
 * The command that makes the way back from a painted door.
 *
 * @param world The world.
 * @param plan The door, the sides, and the caller's ids, names and fields.
 * @param arrivalType The entity type the game treats as an arrival point.
 * @returns One compound command; its inverse (`invertCommand`) takes it all back.
 * @throws Error when the map holds no such door, the door paints no cell, its destination holds no such arrival
 *   point, the new arrival point or the way back would stand off its map, or the way back's cell holds a door.
 */
export function wayBackCommand(world: WorldDocument, plan: WayBackPlan, arrivalType: string): Command {
  const map = requireMap(world, plan.mapId);
  const layer = map.layers.find((candidate): candidate is DoorLayer => candidate.kind === "door" && candidate.doors.some((door) => door.id === plan.doorId));
  const door = layer?.doors.find((candidate) => candidate.id === plan.doorId);
  if (layer === undefined || door === undefined) {
    throw new Error(`map ${map.id} holds no door ${plan.doorId}`);
  }
  const box = paintedBox(map, layer, door);
  const target = requireMap(world, door.to.map);
  const chosen = target.layers.flatMap((candidate) => (candidate.kind === "entity" ? candidate.entities : [])).find((entity) => entity.id === door.to.arrival && entity.type === arrivalType);
  if (chosen === undefined) {
    throw new Error(`map ${target.id} holds no arrival point ${door.to.arrival}; choose the door's arrival point first`);
  }
  const spot = outsideOf(box, plan.outside);
  if (!onMap(map, spot.x, spot.y)) {
    throw new Error(`the arrival point would stand outside map ${map.id}; choose another side`);
  }
  const [dx, dy] = STEPS[plan.behind];
  const column = Math.floor(chosen.x * COLLISION_CELLS_PER_TILE) + dx * COLLISION_CELLS_PER_TILE;
  const row = Math.floor(chosen.y * COLLISION_CELLS_PER_TILE) + dy * COLLISION_CELLS_PER_TILE;
  const width = target.width * COLLISION_CELLS_PER_TILE;
  if (column < 0 || row < 0 || column >= width || row >= target.height * COLLISION_CELLS_PER_TILE) {
    throw new Error(`the way back would stand outside map ${target.id}; choose another side`);
  }
  const index = row * width + column;
  const folded = foldDoors(target);
  const holder = folded.doors[(folded.cells[index] as number) - 1];
  if (holder !== undefined) {
    throw new Error(`the way back's cell on map ${target.id} holds door ${holder.id}; choose another side`);
  }
  const doorLayers = target.layers.filter((candidate): candidate is DoorLayer => candidate.kind === "door");
  const into = doorLayers.at(-1);
  const layerId = into?.id ?? plan.doorLayer.id;
  const back: DoorDefinition = { id: plan.door.id, name: plan.door.name, to: { map: map.id, arrival: plan.arrival.id }, fields: { ...door.fields } };
  const commands: Command[] = [
    ...placeMarker(map, { id: plan.arrival.id, type: arrivalType, x: spot.x, y: spot.y, fields: plan.arrival.fields }, plan.markerLayer),
    ...(plan.second === undefined ? [] : placeMarker(target, { id: plan.second.id, type: arrivalType, x: plan.second.x, y: plan.second.y, fields: plan.second.fields }, plan.markerLayer)),
    ...(into === undefined ? [{ kind: "addLayer" as const, mapId: target.id, index: target.layers.length, layer: { kind: "door" as const, id: plan.doorLayer.id, name: plan.doorLayer.name, doors: [], cells: new Uint8Array(width * target.height * COLLISION_CELLS_PER_TILE) } }] : []),
    { kind: "addDoor", mapId: target.id, layerId, index: into?.doors.length ?? 0, door: back },
    { kind: "paintDoor", mapId: target.id, layerId, cells: [{ index, before: null, after: back.id }] },
  ];
  return { kind: "compound", commands };
}
